/* ---------- Components ---------- */
import BaseTableComponent from '../components/BaseTableComponent';

/* ---------- Utils ---------- */
import { kebabCase } from '../../../utils/StringUtils';
import helperUtil from '../utils/helper.util';
import _ from 'lodash';

const POLAR_SORT_KEYS = {
  // @todo make these constants in config
  'sortable-ascending': 'sortable-descending',
  'sortable-descending': 'sortable-ascending'
};

class EnhancedTable extends BaseTableComponent {
  constructor(props) {
    super(props);

    this.state = {
      displayedData: Array.isArray(props.rows) ? props.rows : [],
      sortColumn: props.defaultSortColumn || null
    };
  }

  componentDidMount() {
    const { rows } = this.props;
    const isSortable = this.doRowsHaveSortableColumns;

    if (rows.length > 0 && isSortable) {
      this.setDisplayedData('sort');
    }
  }

  get doRowsHaveSortableColumns() {
    const { headers, defaultSortColumn } = this.props;
    const isSortable =
      headers &&
      defaultSortColumn &&
      Object.prototype.hasOwnProperty.call(defaultSortColumn, 'index') &&
      defaultSortColumn.index >= 0 &&
      defaultSortColumn.index < headers.length &&
      defaultSortColumn.type &&
      headers[defaultSortColumn.index] &&
      headers[defaultSortColumn.index].sortable === true;

    return isSortable;
  }

  getSnapshotBeforeUpdate(prevProps) {
    const { filters } = prevProps;
    const newFilters = [...(this.props.filters || [])];
    const { rows } = prevProps;
    const newRows = this.props.rows;
    const _haveRowsChanged = (r, newR) =>
      r.length !== newR.length ||
      !_.isEqual(this.getRowsForView(r), this.getRowsForView(newR));
    const _haveFiltersChanged = (f, newF) => {
      if (f.length !== newF.length) return true;

      // same length
      for (let i = 0; i < newF.length; i++) {
        const tNewFilter = newF[i];
        const tNewFilterValue =
          tNewFilter && tNewFilter.value ? tNewFilter.value : null;
        const oldFiltered = filters.filter(
          filter => filter.id === tNewFilter.id
        );
        // compare values
        if (oldFiltered && oldFiltered.length > 0 && oldFiltered[0].value) {
          if (tNewFilterValue !== oldFiltered[0].value) return true;
        }
      }
      return false;
    };
    if (filters && newFilters && _haveFiltersChanged(filters, newFilters)) {
      this.setDisplayedData('filter');
    }
    if (rows && newRows && _haveRowsChanged(rows, newRows)) {
      this.setDisplayedData('reset');
    }

    return null;
  }

  updateDisplayedData = () => {
    const { displayedData } = this.state;
    this.updateCallback('updateDisplayedData', displayedData).catch(() => {
      // handle err
    });
  };

  onFilterChange = (value, filter, rowIdx, cellIdx) => {
    const { filters } = this.props;
    const { headers, onPageChange } = this.props;
    const activeHeaderId =
      headers && headers[cellIdx] && headers[cellIdx].id
        ? headers[cellIdx].id
        : null;

    let newFilters = _.cloneDeep(filters);

    function _isValidFilter(v) {
      if (!v) {
        return false;
      }
      if (Array.isArray(v) && v.length === 0) return false;
      if (v && typeof v === 'string' && !(v.length > 0)) {
        return false;
      }

      return true;
    }
    function __getFilterInState() {
      const temp = [...newFilters];
      const filtered = temp.filter(
        tempFilter => tempFilter.columnIdx === cellIdx
      );
      const f = filtered.length ? filtered[0] : null;
      return f;
    }

    const foundFilter = __getFilterInState();
    const filterIdx = foundFilter ? newFilters.indexOf(foundFilter) : -1;
    const isValidFilter = _isValidFilter(value);

    if (!isValidFilter && foundFilter && filterIdx >= 0) {
      // remove filter and set filter to filterInput or filterDropdown
      newFilters.splice(filterIdx, 1);
    } else if (!isValidFilter) {
      return;
    } else if (isValidFilter && foundFilter) {
      // update filter value
      foundFilter.value = value;
      foundFilter.active = filter.active;
      newFilters[filterIdx] = foundFilter;
    } else if (isValidFilter) {
      // add filter if it doesn't already exist
      newFilters = newFilters.concat([
        {
          ...filter,
          id: activeHeaderId,
          columnIdx: cellIdx,
          value
        }
      ]);
    }

    this.updateCallback('updateFilteredData', newFilters)
      .then(() => onPageChange?.(1)) // for getRowsForView())
      .then(() =>
        // focus on button filter.label is only used for missing id (backward compatability)
        document.body
          .querySelector(
            `button[id= ${filter.id || kebabCase(filter.label)}], input[id= ${
              filter.id
            }]`
          )
          ?.focus()
      )
      .catch(() => {
        // error handling?
      });
  };

  getSortMethod = () => {
    const { getSortMethod } = this.props;
    const { sortColumn } = this.state;
    let method;
    const sortDictionary = {
      default: (a, b) => {
        if (sortColumn) {
          const aCol = a.fields[sortColumn.index].value;
          const bCol = b.fields[sortColumn.index].value;
          const ret = sortColumn.type === 'sortable-ascending' ? 1 : -1;
          if (aCol > bCol) return ret;
          if (aCol < bCol) return -ret;
        }
        return 0;
      }
    };

    if (getSortMethod) {
      method = getSortMethod(sortColumn);
    }
    if (!method) method = sortDictionary.default;

    return method;
  };

  onSort = sortInfo => {
    const { sortColumn } = this.state;
    const { headerId, headerIdx } = sortInfo;
    let newType = 'sortable-ascending';
    if (sortColumn && sortColumn.id && sortColumn.id === headerId) {
      newType = POLAR_SORT_KEYS[sortColumn.type];
    }
    const newSortColumn = {
      index: headerIdx,
      id: headerId,
      type: newType
    };
    this.setState(
      {
        sortColumn: newSortColumn
      },
      () => {
        this.props.onPageChange?.(1);
        this.setDisplayedData('sort');
        this.updateCallback('onSort', newSortColumn);
      }
    );
  };

  /**
   * Returns data to set in state. Used by main functions for filtering, sorting, etc
   */
  setDisplayedData = updateType => {
    const that = this;
    let { displayedData } = this.state;
    const { rows } = this.props;
    const cbDictionary = {
      filter: () => that.updateDisplayedData(),
      sort: () => {},
      reset: () => {
        const isSortable = that.doRowsHaveSortableColumns;
        if (isSortable) that.setDisplayedData('sort');
      }
    };

    const cb =
      updateType && cbDictionary[updateType] ? cbDictionary[updateType] : null;
    switch (updateType) {
      case 'filter':
        displayedData = this.setFilteredData();
        break;
      case 'sort':
        displayedData = this.setSortedData();
        break;
      case 'reset':
        displayedData = rows;
        break;
      default:
        return;
    }

    this.setState(
      {
        displayedData
      },
      cb
    );
  };

  /**
   * Splices rows with page size so not all rows are passsed into base grid. Helps memory and base grid
   * is responsible for just rendering passed in rows. Makes it more dumb
   */
  getRowsForView = (dataSet = this.state.displayedData) => {
    const { isControlledPagination, pageSize, page } = this.props;
    if (isControlledPagination) return dataSet;

    const startIdx = (page - 1) * pageSize; // pagination component is not 0 indexed
    const endIdxValue = startIdx + pageSize;
    const endIdxTest = dataSet.length <= endIdxValue;
    const endIdx = endIdxTest ? dataSet.length : endIdxValue;
    if (dataSet && dataSet.length > 0) {
      dataSet = dataSet.slice(startIdx, endIdx);
      return dataSet;
    }

    return [];
  };

  /**
   * Gets proper filter method from prop function getFilterMethod which is a dictionary for filter methods
   * from the cell filter in state which has the id of the header
   */
  getFilterMethod = cellFilter => {
    const { getFilterMethod } = this.props;
    const filterDictionary = {
      filterInput: row => {
        const data =
          row[cellFilter.columnIdx] && row[cellFilter.columnIdx].value
            ? row[cellFilter.columnIdx].value
            : null;

        return (
          data &&
          typeof data === 'string' &&
          data.toLowerCase().includes(cellFilter.value.toLowerCase())
        );
      }
      /* Provide standard for status badge filtering */
      // statusBadge: row => { // @TODO should filter text and status ?
      //   const data =
      //     row[cellFilter.columnIdx] && row[cellFilter.columnIdx].value
      //       ? row[cellFilter.columnIdx].value
      //       : null;
      //   return true;
      // }
    };

    const defaultFilterTypes = Object.keys(filterDictionary);

    let method =
      getFilterMethod && typeof getFilterMethod === 'function'
        ? getFilterMethod(cellFilter)
        : null;

    if (
      !method ||
      (!(typeof method === 'function') &&
        defaultFilterTypes.includes(cellFilter.type))
    ) {
      // set default method here
      method = filterDictionary[cellFilter.type];
    }

    return method;
  };

  setFilteredData = () => {
    const { filters, rows } = this.props;
    const { sortColumn } = this.state;
    let finalFiltered;

    if (filters && filters.length > 0) {
      finalFiltered = filters.reduce((acc, filter) => {
        const filterMethod = this.getFilterMethod(filter);
        const newRows =
          filterMethod && typeof filterMethod === 'function'
            ? acc.filter(row => filterMethod(row.fields))
            : rows;

        // sort the filtered rows!!!
        if (sortColumn) {
          const sortMethod = this.getSortMethod();
          newRows.sort(sortMethod);
        }
        return newRows;
      }, rows);
    } else {
      // there are no filters reset data
      finalFiltered = rows;
      if (sortColumn) {
        const sortMethod = this.getSortMethod();
        finalFiltered.sort(sortMethod);
      }
    }
    return finalFiltered;
  };

  setSortedData = () => {
    const { displayedData } = this.state;
    const sortMethod = this.getSortMethod();
    displayedData.sort(sortMethod);
    return displayedData;
  };

  /**
   * Includes row action headers not just the header config passed
   */
  getResolvedHeaders = () => {
    const { headers, rowActionConfig } = this.props;

    if (rowActionConfig) {
      return rowActionConfig.concat(headers);
    }

    return headers;
  };

  isRowActionIdx = idx => {
    const { rowActionConfig } = this.props;
    if (
      rowActionConfig &&
      helperUtil.isNonEmptyArray(rowActionConfig) &&
      idx < rowActionConfig.length
    ) {
      return true;
    }
    return false;
  };

  // getHeaderInfoByIndex = idx => {
  //   const resolvedHeaders = this.getResolvedHeaders();
  //   const headerInfo =
  //     resolvedHeaders && resolvedHeaders[idx] ? resolvedHeaders[idx] : null;
  //   return headerInfo;
  // };

  renderTableFilter = renderCell => (header, idx) => {
    const currentFilter =
      header.filter && header.filter.type
        ? { ...header.filter, label: header?.label }
        : null;
    return typeof renderCell === 'function' && currentFilter
      ? this.renderCell(currentFilter, idx, 1, this.onFilterChange)
      : null;
  };

  toggleSelectAll = () => {
    const { selected } = this.props;
    const { displayedData } = this.state;
    const startIndex = this.getIndexForArrayOperations('default', 'start');
    const endIndex = this.getIndexForArrayOperations('default', 'end');
    let updatedSelected = [...selected];

    if (this.getSelectAll()) {
      // map through displayed data find index and remove it from selected
      for (let i = startIndex; i < endIndex; i++) {
        const currRow = displayedData[i];
        const rowIdIdxInSelected = updatedSelected.indexOf(currRow.id);

        if (rowIdIdxInSelected > -1) {
          updatedSelected = [
            ...updatedSelected.slice(0, rowIdIdxInSelected),
            ...updatedSelected.slice(
              rowIdIdxInSelected + 1,
              updatedSelected.length
            )
          ];
        }
      }
    } else {
      for (let i = startIndex; i < endIndex; i++) {
        // add displayed data id to selected
        const currRow = displayedData[i];
        if (!currRow || currRow.id === undefined) break;
        if (updatedSelected.indexOf(currRow.id) === -1) {
          updatedSelected.push(currRow.id);
        }
      }
    }
    this.updateCallback('onSelectRow', updatedSelected);
  };

  /**
   * @param type is defined type
   * @param method is either start or end
   */
  getIndexForArrayOperations = (type, method) => {
    const { page, pageSize } = this.props;
    const { displayedData } = this.state;
    const DEFAULT_START = (page - 1) * pageSize;

    const dictionary = {
      default: {
        start: () => DEFAULT_START,
        end: () => {
          let endIdx = DEFAULT_START + pageSize;

          if (displayedData.length > 0 && displayedData.length < endIdx) {
            endIdx = DEFAULT_START + (displayedData.length - DEFAULT_START);
          }
          return endIdx;
        }
      }
    };

    if (
      dictionary &&
      type &&
      Object.keys(dictionary).includes(type) &&
      dictionary[type][method]
    ) {
      const func = dictionary[type][method];
      if (func && typeof func === 'function') {
        return func();
      }
    }

    return null;
  };

  isSelectedInPage = () => {
    // check if any index in range exists
    const { page, pageSize, selected } = this.props;
    const { displayedData } = this.state;
    const startIdx = (page - 1) * pageSize;
    let endIdx = startIdx + pageSize;
    if (displayedData.length < pageSize) {
      endIdx = startIdx + displayedData.length;
    }

    for (let i = startIdx; i < endIdx; i++) {
      const rowValue = displayedData[i];
      if (
        rowValue &&
        (rowValue.id || rowValue.id === 0) &&
        selected.indexOf(rowValue.id) > -1
      ) {
        return true;
      }
    }

    return false;
  };

  isSelected = key => {
    const { selected } = this.props;
    /*
      Instead of passing our external selection state we provide an 'isSelected'
      callback and detect the selection state ourselves. This allows any implementation
      for selection (either an array, object keys, or even a Javascript Set object).
    */
    return selected.includes(key);
  };

  getSelectAll = () => {
    const { selected } = this.props;
    const { displayedData } = this.state;

    // for looping through page data
    const startIdx = this.getIndexForArrayOperations('default', 'start');
    const endIdx = this.getIndexForArrayOperations('default', 'end');

    if (!(displayedData && displayedData.length)) return false;

    for (let i = startIdx; i < endIdx; i++) {
      const rowValue = displayedData[i];
      // go through page data
      if (!rowValue || (Number.isNaN(rowValue.id) && !rowValue.id))
        return false;
      if (selected.indexOf(rowValue.id) === -1) {
        return false;
      }
    }

    return true;
  };

  selectRow = key => {
    const { selected } = this.props;
    /*
      Implementation of how to manage the selection state is up to the developer.
      This implementation uses an array stored in the component state.
      Other implementations could use object keys, a Javascript Set, or Redux... etc.
    */
    // start off with the existing state
    let updatedSelected = [...selected];
    const keyIndex = updatedSelected.indexOf(key);
    // check to see if the key exists
    if (keyIndex >= 0) {
      // index does exist so we will remove it using destructing
      updatedSelected = [
        ...updatedSelected.slice(0, keyIndex),
        ...updatedSelected.slice(keyIndex + 1)
      ];
    } else {
      // index does not exist so add it
      updatedSelected.splice(key, 0, key);
    }
    this.updateCallback('onSelectRow', updatedSelected); // @TODO refactor
  };

  invokeCallback = (callback, data) => {
    if (callback && typeof callback === 'function') {
      return callback(data);
    }
    return Promise.reject(this.errors.CALLBACK_TYPE);
  };

  updateCallback = (callbackProperty, data) => {
    if (
      callbackProperty &&
      typeof callbackProperty === 'string' &&
      this.props[callbackProperty]
    ) {
      return Promise.resolve(
        this.invokeCallback(this.props[callbackProperty], data)
      );
    }
    return Promise.reject(new Error(this.errors.NO_CALLBACK)); // Do we want it to crash?
  };

  getMinWidth = () => {};

  getCommonProps = () => {
    const {
      getResolvedHeaders,
      getRowsForView,
      getSelectAll,
      isRowActionIdx,
      isSelected,
      isSelectedInPage,
      onSort,
      renderRowAction,
      renderTableFilter,
      selectRow,
      state,
      toggleSelectAll,
      types
    } = this;

    const { displayedData, sortColumn } = state;

    const props = {
      allRowsLength: displayedData.length,
      getSelectAll,
      isRowActionIdx,
      isSelected,
      isSelectedInPage,
      onSort,
      renderRowAction,
      renderTableFilter,
      resolvedHeaders: getResolvedHeaders(),
      rows: getRowsForView(),
      selectRow,
      sortColumn,
      toggleSelectAll,
      variant: types.DISPLAY,
      wrappedState: state
    };

    return { ...this.props, ...props };
  };
}

export default EnhancedTable;
